import { h } from './dom.js';
import { Viewer } from './viewer.js';

// One creation: the viewer, its HUD and camera buttons. The chat (turns) attaches to the returned workspace.
export async function openWorkspace(root, api, creationId) {
  const stage = h('div.stage');
  const hud = h('div.hud', 'Loading…');
  const views = h('div.views');
  const edges = h('input', { type: 'checkbox' });
  const exportButton = h('button', { type: 'button' }, 'Export UV (JPG)');
  const exportStatus = h('span.export-status', { role: 'status' });
  const exportBar = h('div.export', h('label', edges, ' UV edges'), exportButton, exportStatus);
  root.replaceChildren(stage, hud, views, exportBar);

  const viewer = new Viewer(stage);
  for (const name of Viewer.views) views.append(h('button', { type: 'button', onclick: () => viewer.view(name) }, name));

  const [detail, files, { assets }] = await Promise.all([
    api.creation(creationId), api.assetFiles(creationId), api.assets(),
  ]);
  const { creation } = detail;
  const asset = assets.find((a) => a.id === creation.asset_id);
  const mb = (bytes) => (bytes / 1e6).toFixed(1);
  const stats = await viewer.load(files, {
    onProgress: ({ loaded, total }) => {
      hud.textContent = total
        ? `Loading model… ${Math.floor((loaded / total) * 100)}% (${mb(loaded)} / ${mb(total)} MB)`
        : `Loading model… ${mb(loaded)} MB`;
    },
  });

  const workspace = {
    api, viewer, creation, detail, root, exportBar, assetName: asset?.name,
    /** Called with the version number each time the viewer shows another version (the publish dialog follows it). */
    onShow: [],
    /** Show `version` (0 is the untouched asset). Fetches it; one retry with fresh URLs if a download fails. */
    async show(version) {
      if (version === 0) {
        await viewer.showOriginal();
      } else {
        for (let attempt = 0; ; attempt++) {
          const v = await api.version(creationId, version);
          try {
            await viewer.showVersion(v);
            break;
          } catch (err) {
            if (attempt >= 1) throw err;
          }
        }
      }
      for (const listener of workspace.onShow) listener(viewer.shownVersion);
    },
    setHud() {
      hud.textContent = [
        asset?.name ?? creation.asset_id,
        `${asset?.asset_ref ?? creation.asset_id} · contract v${creation.contract_version} · showing v${viewer.shownVersion}`,
        `loaded in ${stats.ms} ms · ${stats.triangles.toLocaleString()} triangles`,
        `texture sets: ${stats.sets.join(', ')}`,
        'drag to orbit · scroll to zoom',
      ].join('\n');
    },
  };
  exportButton.onclick = async () => {
    exportButton.disabled = true;
    exportStatus.textContent = '';
    try {
      const name = `${creation.asset_id}-${creation.id.slice(-8)}-v${viewer.shownVersion}-uv${edges.checked ? '-edges' : ''}.jpg`;
      await viewer.exportUv(name, { edges: edges.checked });
    } catch (err) {
      exportStatus.textContent = `Export failed: ${err.message}`;
    } finally {
      exportButton.disabled = false;
    }
  };

  await workspace.show(creation.latest_version);
  workspace.setHud();
  return workspace;
}
